import BroadcastsAPI from '../../api/broadcasts';
import {
  createAccountContextGuard,
  createAccountRequestGuard,
} from './utils/accountRequestGuard';

const listRequestGuard = createAccountRequestGuard();
const mutationContextGuard = createAccountContextGuard();

const beginMutationRequest = (localState, rootGetters, payload) => {
  const normalizedPayload =
    payload && typeof payload === 'object' ? payload : { id: payload };
  const { id, accountId, generation } = normalizedPayload;
  const accountRequestEpoch = localState.accountRequestEpoch;
  const request = mutationContextGuard.begin(localState, {
    accountId: accountId ?? rootGetters.getCurrentAccountId,
    generation,
  });

  return {
    id,
    accountId: request.accountId,
    isCurrent: () =>
      localState.accountRequestEpoch === accountRequestEpoch &&
      mutationContextGuard.isCurrent(localState, request, rootGetters),
  };
};

export const state = {
  accountRequestEpoch: 0,
  records: [],
  selected: null,
  contacts: [],
  contactsMeta: { total_count: 0, current_page: 1 },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingContacts: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAllBroadcasts(_state) {
    return _state.records;
  },
  getSelected(_state) {
    return _state.selected;
  },
  getBroadcastContacts(_state) {
    return _state.contacts;
  },
  getContactsMeta(_state) {
    return _state.contactsMeta;
  },
};

export const actions = {
  async list(
    { commit, state: localState = state, rootGetters = {} },
    params = {}
  ) {
    const { accountId, generation, ...apiParams } = params;
    const request = listRequestGuard.begin(localState, {
      accountId: accountId ?? rootGetters.getCurrentAccountId,
      generation,
    });
    const isCurrentRequest = () =>
      listRequestGuard.isCurrent(localState, request, rootGetters);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await BroadcastsAPI.list(apiParams);
      if (isCurrentRequest()) {
        commit('SET_BROADCASTS', response.data);
      }
      return response.data;
    } finally {
      if (isCurrentRequest()) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  // Sem try/catch: os erros do Axios propagam intactos para o
  // componente, que exibe error.response.data.error. O wrapper antigo
  // (throw new Error(error)) virava "[object Object]" e escondia a
  // mensagem real de validação do backend.

  async get({ commit, state: localState = state, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.show(request.id);
    if (request.isCurrent()) {
      commit('UPDATE_BROADCAST', response.data);
    }
    return response.data;
  },

  async create(
    { commit, state: localState = state, rootGetters = {} },
    broadcastData = {}
  ) {
    const { accountId, generation, ...apiData } = broadcastData;
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await BroadcastsAPI.create(apiData);
      if (request.isCurrent()) {
        commit('ADD_BROADCAST', response.data);
      }
      return response.data;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isCreating: false });
      }
    }
  },

  async update(
    { commit, state: localState = state, rootGetters = {} },
    { id, accountId, generation, ...updateData }
  ) {
    const request = beginMutationRequest(localState, rootGetters, {
      id,
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await BroadcastsAPI.update(id, updateData);
      if (request.isCurrent()) {
        commit('UPDATE_BROADCAST', response.data);
      }
      return response.data;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isUpdating: false });
      }
    }
  },

  async delete({ commit, state: localState, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      const response = await BroadcastsAPI.delete(request.id, {
        accountId: request.accountId,
      });
      if (request.isCurrent()) {
        commit('REMOVE_BROADCAST', request.id);
      }
      return response?.data;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isDeleting: false });
      }
    }
  },

  async duplicate({ commit, state: localState, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.duplicate(request.id, {
      accountId: request.accountId,
    });
    if (request.isCurrent()) {
      commit('ADD_BROADCAST', response.data);
    }
    return response.data;
  },

  async pause({ commit, state: localState, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.pause(request.id, {
      accountId: request.accountId,
    });
    if (request.isCurrent()) {
      commit('UPDATE_BROADCAST', response.data);
    }
    return response.data;
  },

  async resume({ commit, state: localState, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.resume(request.id, {
      accountId: request.accountId,
    });
    if (request.isCurrent()) {
      commit('UPDATE_BROADCAST', response.data);
    }
    return response.data;
  },

  async cancel({ commit, state: localState, rootGetters = {} }, payload) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.cancel(request.id, {
      accountId: request.accountId,
    });
    if (request.isCurrent()) {
      commit('UPDATE_BROADCAST', response.data);
    }
    return response.data;
  },
  async retryFailed(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const request = beginMutationRequest(localState, rootGetters, payload);
    const response = await BroadcastsAPI.retryFailed(request.id);
    if (request.isCurrent()) {
      commit('UPDATE_BROADCAST', response.data);
    }
    return response.data;
  },

  async contactsList(
    { commit, state: localState = state, rootGetters = {} },
    { id, params = {}, accountId, generation } = {}
  ) {
    const request = beginMutationRequest(localState, rootGetters, {
      id,
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isFetchingContacts: true });
    try {
      const response = await BroadcastsAPI.contacts(id, params);
      const payload = response.data;
      if (request.isCurrent()) {
        commit('SET_CONTACTS', payload.data || payload);
        commit('SET_CONTACTS_META', {
          total_count: payload.total_count || (payload.data || payload).length,
          current_page: params.page || 1,
        });
      }
      return payload;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isFetchingContacts: false });
      }
    }
  },

  async exportCsv(_, id) {
    return BroadcastsAPI.exportCsv(id);
  },

  setSelected({ commit }, broadcast) {
    commit('SET_SELECTED', broadcast);
  },
};

export const mutations = {
  SET_UI_FLAG(_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  SET_BROADCASTS(_state, broadcasts) {
    _state.records = broadcasts;
  },

  RESET_ACCOUNT_DATA(_state) {
    _state.accountRequestEpoch = (_state.accountRequestEpoch || 0) + 1;
    _state.records = [];
    _state.selected = null;
    _state.contacts = [];
    _state.contactsMeta = { total_count: 0, current_page: 1 };
    _state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetchingContacts: false,
    };
  },

  ADD_BROADCAST(_state, broadcast) {
    _state.records.push(broadcast);
  },

  UPDATE_BROADCAST(_state, updatedBroadcast) {
    const index = _state.records.findIndex(r => r.id === updatedBroadcast.id);
    if (index !== -1) {
      _state.records.splice(index, 1, updatedBroadcast);
    }
    if (_state.selected && _state.selected.id === updatedBroadcast.id) {
      _state.selected = updatedBroadcast;
    }
  },

  REMOVE_BROADCAST(_state, id) {
    _state.records = _state.records.filter(r => r.id !== id);
    if (_state.selected && _state.selected.id === id) {
      _state.selected = null;
    }
  },

  SET_SELECTED(_state, broadcast) {
    _state.selected = broadcast;
  },

  SET_CONTACTS(_state, contacts) {
    _state.contacts = contacts;
  },

  SET_CONTACTS_META(_state, meta) {
    _state.contactsMeta = meta;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
